// The header, as today's (components/Header.tsx there): Back and Forward in
// the Mac app's workspace, the logo and the app's name (the Board), the
// project picker, the project's tabs, the live pill, and the person's menu.
// Its band is the window's to drag by in the Mac app (`data-drag-region`).
// Search (⌘K) reads the issues already in the store. Needs you is gone
// (Poulpe-406): a session's question docks in its pane, and an approval is
// on the Board.
import { createContext, useContext, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import type { Me } from '../api/types'
import { isIssueRef, isSheet } from '../lib/keys'
import { setThemeChoice, useThemeChoice } from '../lib/theme'
import { projectForPath, projectPage, projectPath, PROJECT_PAGES } from '../projects/routing'
import { useBoardState, useSelectedProject, useStore } from '../store/context'
import { needsYou } from '../store/store'
import { landingsAwaitingPerson } from '../sessions/attention'
import { cx } from '../transcript/bits'
import { BoardLogo, CheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, SearchIcon } from './Icons'
import { Popover } from './Popover'
import { APP_NAME } from './useDocumentTitle'
import { settingsPath } from '../settings/routing'
import { NotSynced } from './NotSynced'

export const WorkspaceHistory = createContext<{ back: boolean; forward: boolean; go(step: number): void } | null>(null)

interface Tab {
  to: string
  label: string
}

/** A project's tabs, in the order they give way to More: Code is the last to. */
const TABS: readonly Tab[] = [
  { to: '/code', label: 'Code' },
  { to: '/board', label: 'Board' },
  { to: '/plans', label: 'Plans' },
  { to: '/docs', label: 'Docs' },
]

export function Header({ onSearch }: { onSearch?: () => void }) {
  const { projects, me } = useBoardState()
  const selected = useSelectedProject()
  const { pathname } = useLocation()
  const workspace = useContext(WorkspaceHistory)
  const project = projectForPath(pathname, [...projects.values()]) ?? selected
  const tabs = TABS.map((tab) => ({ ...tab, to: project ? projectPath(project.key, tab.to) : tab.to }))
  return (
    <>
    <header className="app-header" data-drag-region>
      {workspace && <HistoryButtons />}
      <Link to={project ? projectPath(project.key, '/board') : '/board'} className="brand" aria-label={`${APP_NAME}, Board`}>
        <BoardLogo size={20} />
        {/* The app's own name, whatever the workspace is called: the company's
            name is in the person's menu (Fabien, 25 Oct). */}
        <span className="workspace-name">{APP_NAME}</span>
      </Link>
      {projects.size < 2 && <ProjectPicker counts={null} />}
      <MainNav tabs={tabs} />
      <button type="button" className="search-box" onClick={onSearch} disabled={isSheet(pathname)} aria-label="Search" aria-keyshortcuts="Meta+K Control+K">
        <SearchIcon size={14} />
        <span className="search-box-text">Search</span>
        <kbd>⌘K</kbd>
      </button>
      <LivePill />
      {me?.member && <PersonMenu me={me} />}
    </header>
    <NotSynced />
    </>
  )
}

function ProjectPicker({ counts }: { counts: ReadonlyMap<string, number> | null }) {
  const { projects } = useBoardState()
  const selected = useSelectedProject()
  const store = useStore()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const all = [...projects.values()]
  const project = projectForPath(pathname, all) ?? selected
  return (
    <Popover
      label="Project"
      buttonLabel="Project"
      className="project-control"
      buttonClassName="project-picker"
      button={<><span>{project?.name ?? 'Add a project'}</span><ChevronDownIcon size={12} /></>}
    >
      {(close) => (
        <div className="project-menu" role="menu" onKeyDown={stepThroughMenu}>
          {all.map((item) => (
            <button
              type="button"
              className="theme-item"
              role="menuitemradio"
              aria-checked={item.id === project?.id}
              key={item.id}
              onClick={() => {
                if (item.id === project?.id) { close(); return }
                const page = projectPage(pathname)
                // A session belongs to the old project; switching opens the other project's tab.
                const kind = PROJECT_PAGES.find((name) => page === `/${name}` || page.startsWith(`/${name}/`))
                // A plan is its project's: the other project's Plans tab opens.
                if (kind) navigate(projectPath(item.key, kind === 'plan' ? '/plans' : `/${kind}`))
                else {
                  if (isIssueRef(pathname.slice(1), all.map((p) => p.key))) navigate(projectPath(item.key, '/board'))
                  else store.selectProject(item.id)
                }
                close()
              }}
            >
              <span className="theme-item-name">{item.name}</span>
              {all.length > 1 && counts !== null && <>{' '}<span className="nav-badge">{counts.get(item.id) ?? 0}</span></>}
              {item.id === project?.id && <CheckIcon size={13} />}
            </button>
          ))}
          <Link className="theme-item" role="menuitem" to={settingsPath('workspace', 'projects')} onClick={close}>Add a project</Link>
        </div>
      )}
    </Popover>
  )
}

/** In a menu, Up and Down move among its items and round its ends, Home and End go to them. */
function stepThroughMenu(event: ReactKeyboardEvent<HTMLElement>) {
  const items = [...event.currentTarget.querySelectorAll<HTMLElement>('[role="menuitemradio"], [role="menuitem"]')]
  const at = items.indexOf(document.activeElement as HTMLElement)
  const last = items.length - 1
  let to: number
  if (event.key === 'ArrowDown') to = at < 0 || at === last ? 0 : at + 1
  else if (event.key === 'ArrowUp') to = at <= 0 ? last : at - 1
  else if (event.key === 'Home') to = 0
  else if (event.key === 'End') to = last
  else return
  event.preventDefault()
  items[to]?.focus()
}

/**
 * Back and Forward, in the Mac app's workspace only (SUN-1119): its window
 * has no toolbar, and a browser has its own. Each is disabled while the
 * tab's history has nowhere to go that way. View › Back and Forward (⌘[ and
 * ⌘]) and the swipe do the same.
 */
export function HistoryButtons() {
  const history = useContext(WorkspaceHistory)
  if (!history) return null
  return (
    <div className="history-buttons">
      <button type="button" className="history-button" aria-label="Back" title="Back (⌘[)" aria-keyshortcuts="Meta+[" disabled={!history.back} onClick={() => history.go(-1)}>
        <ChevronLeftIcon size={16} />
      </button>
      <button type="button" className="history-button" aria-label="Forward" title="Forward (⌘])" aria-keyshortcuts="Meta+]" disabled={!history.forward} onClick={() => history.go(1)}>
        <ChevronRightIcon size={16} />
      </button>
    </div>
  )
}

/**
 * How many tabs fit in the nav as laid out now; when not all do, More's
 * width is kept for the rest. The project picker, among several, always
 * stays.
 */
export function fittingTabs(nav: HTMLElement): number {
  const widths = [...nav.querySelectorAll<HTMLElement>(':scope > [data-tab]')].map((tab) => tab.offsetWidth)
  const fixed = [...nav.querySelectorAll<HTMLElement>(':scope > .nav-project')]
  const more = nav.querySelector<HTMLElement>(':scope > [data-more]')
  const gap = parseFloat(getComputedStyle(nav).columnGap) || 0
  const fixedWidth = fixed.reduce((sum, item) => sum + item.offsetWidth, 0)
  const span = (n: number) => fixedWidth + widths.slice(0, n).reduce((sum, width) => sum + width, 0) + gap * Math.max(0, fixed.length + n - 1)
  if (span(widths.length) <= nav.clientWidth) return widths.length
  const room = nav.clientWidth - (more?.offsetWidth ?? 0) - gap
  let fit = 0
  while (fit < widths.length && span(fit + 1) <= room) fit += 1
  return fit
}

/**
 * The main tabs. They show while they fit, and those that do not (on a
 * phone, in a narrow window) wait behind More, so each stays within
 * reach. A tab that gives way keeps its place in the bar, out of sight, so
 * its width can be measured again.
 */
function MainNav({ tabs }: { tabs: readonly Tab[] }) {
  const { projects } = useBoardState()
  const nav = useRef<HTMLElement>(null)
  const [shown, setShown] = useState(tabs.length)
  const waiting = useWaitingByProject()

  useLayoutEffect(() => {
    const el = nav.current
    if (!el || typeof ResizeObserver === 'undefined') {
      setShown(tabs.length)
      return
    }
    const measure = () => setShown(fittingTabs(el))
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [tabs])

  return (
    <nav className="app-nav" aria-label="Main" ref={nav}>
      {projects.size > 1 && <div className="nav-project"><ProjectPicker counts={waiting} /></div>}
      {tabs.map((tab, i) => {
        const away = i >= shown
        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            data-tab
            className={({ isActive }) => cx(isActive && 'active', away && 'is-overflow')}
            aria-hidden={away || undefined}
            tabIndex={away ? -1 : undefined}
          >
            {tab.label}
          </NavLink>
        )
      })}
      <MoreTabs tabs={tabs.slice(shown)} />
    </nav>
  )
}

/** The tabs that did not fit, behind a More button; it reads as current when one of them is. */
function MoreTabs({ tabs }: { tabs: readonly Tab[] }) {
  const { pathname } = useLocation()
  const current = tabs.find((tab) => pathname === tab.to || pathname.startsWith(`${tab.to}/`))
  const idle = tabs.length === 0
  return (
    <div className={cx('nav-more', idle && 'is-overflow')} data-more aria-hidden={idle || undefined}>
      <Popover
        label="More pages"
        align="end"
        buttonClassName={cx('nav-more-button', current && 'is-current')}
        button={
          <>
            More
            {current && <span className="visually-hidden">{`, ${current.label} is open`}</span>}
            <ChevronDownIcon size={12} />
          </>
        }
      >
        {(close) => (
          <ul className="nav-more-list">
            {tabs.map((tab) => {
              return (
                <li key={tab.to}>
                  <NavLink to={tab.to} onClick={close}>
                    {tab.label}
                  </NavLink>
                </li>
              )
            })}
          </ul>
        )}
      </Popover>
    </div>
  )
}

/** The questions, landing approvals and stopped batches waiting for the person, by project's id: what the picker counts among several projects. */
function useWaitingByProject(): ReadonlyMap<string, number> {
  const { questions, pendingAnswers, sessions, landings, held, dismissed, me } = useBoardState()
  const member = me?.member?.id
  const projects = new Map<string, number>()
  for (const question of needsYou(questions, member, pendingAnswers)) {
    const project = sessions.get(question.session)?.project_id
    if (project) projects.set(project, (projects.get(project) ?? 0) + 1)
  }
  for (const landing of member ? landingsAwaitingPerson(landings, held, dismissed) : []) {
    projects.set(landing.project_id, (projects.get(landing.project_id) ?? 0) + 1)
  }
  return projects
}

/** How many changes wait to be sent: one change, or several. */
const waitingText = (count: number) => `${count} ${count === 1 ? 'change' : 'changes'} waiting`

/** What the header says while the live updates are interrupted: the store polls, and opens the feed again by itself. */
export function LivePill() {
  const { stream, offline, waiting } = useBoardState()
  if (stream !== 'reconnecting' && !offline && !waiting) return null
  return (
    <span className="sync-pill is-warn" role="status" title={offline || waiting ? undefined : 'Live updates are interrupted; reconnecting.'}>
      <span className="sync-dot" />
      {offline ? 'Offline' : waiting ? waitingText(waiting) : 'Reconnecting…'}
      {offline && waiting > 0 && <span>{` · ${waitingText(waiting)}`}</span>}
    </span>
  )
}

/** The person's menu: Settings, who they are, the theme, and Sign out. */
function PersonMenu({ me }: { me: Me }) {
  const store = useStore()
  const navigate = useNavigate()
  const [leaving, setLeaving] = useState(false)
  const choice = useThemeChoice()
  const name = me.member?.name ?? ''
  return (
    <Popover
      label="Your account"
      align="end"
      className="person-control"
      buttonClassName="person-button"
      buttonLabel={`Your account, ${name}`}
      button={<><span className="person-avatar" aria-hidden="true">{name.slice(0, 1).toUpperCase()}</span><span className="person-name">{name}</span><ChevronDownIcon size={12} /></>}
    >
      {(close) => (
        <div className="person-menu" role="menu" onKeyDown={stepThroughMenu}>
          <Link role="menuitem" className="theme-item" to={settingsPath('you', 'account')} onClick={close}>Settings <kbd>⌘,</kbd></Link>
          <div className="person-menu-identity"><strong>{name}</strong><span>{me.workspace.name}</span></div>
          <p className="person-menu-label">Theme</p>
          {(['system', 'light', 'dark'] as const).map(theme => (
            <button type="button" key={theme} role="menuitemradio" aria-checked={choice === theme} className="theme-item" onClick={() => { setThemeChoice(theme); close() }}>
              <span className="theme-item-name">{theme.charAt(0).toUpperCase() + theme.slice(1)}</span>{choice === theme && <CheckIcon size={13} className="theme-item-tick" />}
            </button>
          ))}
          {!store.byToken && (
            <button type="button" role="menuitem" className="theme-item" disabled={leaving} onClick={async () => {
              setLeaving(true)
              const out = await store.signOut()
              setLeaving(false)
              if (out) { close(); navigate('/') }
            }}>{leaving ? 'Signing out…' : 'Sign out'}</button>
          )}
        </div>
      )}
    </Popover>
  )
}
